import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { toastService } from '../../services/ToastService';
import Tooltip from '../Tooltip/Tooltip';

interface PaymentStatusToggleProps {
  id: number | string;
  paid: boolean;
  onPaymentStatusChange: (id: number | string, paid: boolean) => Promise<void>;
  disabled?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

const PaymentStatusToggle: React.FC<PaymentStatusToggleProps> = ({
  id,
  paid,
  onPaymentStatusChange,
  disabled = false,
  size = 'sm',
  className = ''
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleToggle = async () => {
    if (disabled || isLoading) return;
    
    setIsLoading(true);
    try {
      await onPaymentStatusChange(id, !paid);
      toastService.showToast(
        paid 
          ? t("booking.pending.payment_marked_unpaid")
          : t("booking.pending.payment_marked_paid"),
        "success"
      );
    } catch (error) {
      console.error('Error updating payment status:', error);
      toastService.showToast(t("booking.pending.payment_update_error"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'h-8 w-8';
      case 'default':
        return 'h-10 w-10';
      case 'lg':
        return 'h-12 w-12';
      default:
        return 'h-8 w-8';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'sm':
        return 'h-4 w-4';
      case 'default':
        return 'h-5 w-5';
      case 'lg':
        return 'h-6 w-6';
      default:
        return 'h-4 w-4';
    }
  };

  const tooltipContent = paid 
    ? t("booking.pending.mark_unpaid")
    : t("booking.pending.mark_paid");

  return (
    <Tooltip content={tooltipContent}>
      <button
        className={`${getSizeClasses()} rounded-full flex items-center justify-center ${
          paid 
            ? "bg-red-100 text-red-600 hover:bg-red-200 border border-red-200" 
            : "bg-green-100 text-green-600 hover:bg-green-200 border border-green-200"
        } ${className} ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={handleToggle}
        disabled={disabled || isLoading}
      >
        {isLoading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
        ) : paid ? (
          <X className={`${getIconSize()} text-red-600`} />
        ) : (
          <Check className={`${getIconSize()} text-green-600`} />
        )}
      </button>
    </Tooltip>
  );
};

export default PaymentStatusToggle;
